import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { withRouter, RouteComponentProps } from "react-router-dom";
import PmoTooltip from "../../../ProductMarketingOptimization/components/common/PmoTooltip";
import BrandPerformanceTable from "../../../../components/Tables/BrandPerformanceTable";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import { NoDataMsg } from "../../../../components/AppMessages";

import { getMonthlyTableForDownloadData } from "../../../../api/brandPerformanceApi";
import { RootStateOrAny, useSelector } from "react-redux";
import CompetitorSelect from "../competitorSelect";

interface Props extends RouteComponentProps {
  diff: number;
  onHandleRadioClick: any;
  nissanCompetitor: string;
  granularity: string;
}

const TableForDownloadMonthly = withRouter((props: Props) => {
  const { history, onHandleRadioClick, nissanCompetitor, granularity } = props;
  const params = useSelector((state: RootStateOrAny) => state.parameters);

  const {
    data: monthlyTableForDownloadData,
    isFetching: loadingMonthlyTableForDownloadData,
    refetch: refetchTableForDownloadMonthly,
  } = useQuery(["salesByVolumeTableData", granularity, history.location.search], () => getMonthlyTableForDownloadData(params), {
    enabled: false,
  });

  // when the url changes, refetch the data
  useEffect(() => {
    refetchTableForDownloadMonthly();
  }, [history.location.search]);

  // the backend response returns a dynamic date columns with the format of "yyyy-mm-dd" this helps to filter out the date columns
  const dateKeys =
    monthlyTableForDownloadData?.length > 0
      ? Object.keys(monthlyTableForDownloadData[0]).filter((key) => key.match(/^\d{4}-\d{2}-\d{2}$/))
      : [];

  // Define dynamic date columns
  const dynamicColumns = dateKeys.map((dateKey) => ({
    id: dateKey,
    Header: dateKey,
    accessor: dateKey,
    Cell: (props: { value: number }) => {
      const { value } = props;
      // number is in thousands add commas
      const formattedValue = value?.toLocaleString();
      return formattedValue;
    },
  }));

  const staticColumns = [
    {
      id: "No",
      Header: `No`,
      accessor: "No",

      Cell: (props: { value: string }) => {
        const { value } = props;

        return value;
      },
    },
    {
      Header: "Country",
      accessor: `country`,
      id: "country",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Data Category",
      accessor: `data_category`,
      id: "data_category`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Category Detail",
      accessor: `category_detail`,
      id: "category_detail`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Segment Name",
      accessor: `segment_name`,
      id: "segment_name`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Nissan Brand/Model",
      accessor: `nissan_brand_or_model`,
      id: "nissan_brand_or_model`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Comp Brand",
      accessor: `comp_brand`,
      id: "comp_brand`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Comp Model",
      accessor: `comp_model`,
      id: "comp_model`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
  ];

  const columns = React.useMemo(() => [...staticColumns, ...dynamicColumns], [staticColumns, dynamicColumns]);

  return (
    <div className="table-download-content">
      <div className="primary_background ">
        <div className="header" style={{ padding: "1.5em" }}>
          <div className="title_container">
            <h3>Table for data Download (Sales/TPVA/Media Spend)</h3>
            <CompetitorSelect onHandleRadioClick={onHandleRadioClick} nissanCompetitor={nissanCompetitor} />
          </div>
        </div>
        <div className="table_wrapper">
          <div className="chart_content brand_perf_table" style={{ margin: "1em" }}>
            {monthlyTableForDownloadData?.length && !loadingMonthlyTableForDownloadData ? (
              <BrandPerformanceTable
                isLoading={loadingMonthlyTableForDownloadData}
                columns={columns ?? []}
                tableId="tableForDownloadMonthlyID"
                data={monthlyTableForDownloadData ?? []}
                initialSortCol={{ id: "No", desc: false }}
              />
            ) : loadingMonthlyTableForDownloadData ? (
              <LoadingEllipsis isLoading={loadingMonthlyTableForDownloadData} />
            ) : (
              <NoDataMsg message="No data available" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
export default TableForDownloadMonthly;
